import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MegaphoneIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../lib/api';

const fieldClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm transition placeholder:text-gray-400 ' +
  'focus:border-primary-orange focus:outline-none focus:ring-2 focus:ring-orange-500/25 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500';

export default function GlobalNotificationsPanel() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [titleAr, setTitleAr] = useState('');
  const [messageAr, setMessageAr] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{ recipientCount: number } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const runSend = async () => {
    const titleT = title.trim();
    const messageT = message.trim();
    if (!titleT || !messageT) {
      setError(t('globalNotifications.validationEn'));
      return;
    }
    setSending(true);
    setError(null);
    setLastResult(null);
    setShowConfirm(false);
    try {
      const res = await api.post('/admin/notifications/global', {
        title: titleT,
        message: messageT,
        titleAr: titleAr.trim() || undefined,
        messageAr: messageAr.trim() || undefined,
      });
      const count = res.data?.recipientCount ?? 0;
      setLastResult({ recipientCount: count });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setError(
        typeof msg === 'string'
          ? msg
          : Array.isArray(msg)
            ? msg.join(', ')
            : t('globalNotifications.requestFailed'),
      );
    } finally {
      setSending(false);
    }
  };

  const requestSend = () => {
    const titleT = title.trim();
    const messageT = message.trim();
    if (!titleT || !messageT) {
      setError(t('globalNotifications.validationEn'));
      return;
    }
    setError(null);
    setShowConfirm(true);
  };

  return (
    <div dir={isRtl ? 'rtl' : 'ltr'} className="max-w-5xl mx-auto space-y-6">
      {/* Intro */}
      <div
        className={`relative overflow-hidden rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-amber-50/80 p-6 shadow-sm ${isRtl ? 'text-right' : 'text-left'}`}
      >
        <div
          className={`flex flex-col gap-4 sm:flex-row sm:items-start ${isRtl ? 'sm:flex-row-reverse' : ''}`}
        >
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-orange to-orange-600 text-white shadow-lg shadow-orange-500/25">
            <MegaphoneIcon className="h-8 w-8" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm leading-relaxed text-gray-600 sm:text-[15px]">{t('globalNotifications.subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Bilingual form: fixed LTR column order — English left, Arabic right */}
      <div dir="ltr" className="grid gap-6 lg:grid-cols-2">
        <section
          className={`rounded-2xl border border-gray-200 bg-white p-5 shadow-sm ring-1 ring-gray-100 ${isRtl ? 'lg:order-2' : ''}`}
        >
          <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-gray-100 pb-3">
            <span className="text-sm font-semibold text-gray-900">{t('globalNotifications.sectionEnglish')}</span>
            <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800">
              {t('globalNotifications.badgeRequired')}
            </span>
          </div>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('globalNotifications.titleEn')}
              </label>
              <input
                className={fieldClass}
                dir="ltr"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('globalNotifications.titleEnPlaceholder')}
                disabled={sending}
                autoComplete="off"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('globalNotifications.messageEn')}
              </label>
              <textarea
                className={`${fieldClass} min-h-[120px] resize-y`}
                dir="ltr"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('globalNotifications.messageEnPlaceholder')}
                disabled={sending}
              />
            </div>
          </div>
        </section>

        <section
          className={`rounded-2xl border border-emerald-100/80 bg-gradient-to-b from-emerald-50/40 to-white p-5 shadow-sm ring-1 ring-emerald-100/60 ${isRtl ? 'lg:order-1' : ''}`}
          dir="rtl"
        >
          <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-emerald-100/80 pb-3">
            <span className="text-sm font-semibold text-gray-900">{t('globalNotifications.sectionArabic')}</span>
            <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-900">
              {t('globalNotifications.badgeOptional')}
            </span>
          </div>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('globalNotifications.titleAr')}
              </label>
              <input
                className={fieldClass}
                dir="rtl"
                value={titleAr}
                onChange={(e) => setTitleAr(e.target.value)}
                placeholder={t('globalNotifications.titleArPlaceholder')}
                disabled={sending}
                autoComplete="off"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('globalNotifications.messageAr')}
              </label>
              <textarea
                className={`${fieldClass} min-h-[120px] resize-y`}
                dir="rtl"
                value={messageAr}
                onChange={(e) => setMessageAr(e.target.value)}
                placeholder={t('globalNotifications.messageArPlaceholder')}
                disabled={sending}
              />
            </div>
          </div>
        </section>
      </div>

      {/* Hint + actions */}
      <div
        className={`flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between ${isRtl ? 'sm:flex-row-reverse' : ''}`}
      >
        <div
          className={`flex gap-3 text-sm text-gray-600 ${isRtl ? 'flex-row-reverse text-right' : 'text-left'}`}
        >
          <InformationCircleIcon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden />
          <p className="leading-relaxed">{t('globalNotifications.footerHint')}</p>
        </div>
        <button
          type="button"
          onClick={requestSend}
          disabled={sending}
          className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-orange to-orange-600 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-orange-500/20 transition hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${isRtl ? 'flex-row-reverse' : ''}`}
        >
          <MegaphoneIcon className="h-5 w-5" aria-hidden />
          {sending ? t('globalNotifications.sending') : t('globalNotifications.send')}
        </button>
      </div>

      {error && (
        <div
          role="alert"
          className={`flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 ${isRtl ? 'flex-row-reverse text-right' : ''}`}
        >
          <ExclamationCircleIcon className="h-5 w-5 shrink-0 text-red-600" aria-hidden />
          <span>{error}</span>
        </div>
      )}

      {lastResult && (
        <div
          role="status"
          className={`flex gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 ${isRtl ? 'flex-row-reverse text-right' : ''}`}
        >
          <CheckCircleIcon className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
          <span>{t('globalNotifications.sentToUsers', { count: lastResult.recipientCount })}</span>
        </div>
      )}

      {/* Confirm dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" aria-modal role="dialog">
          <button
            type="button"
            className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px]"
            aria-label={t('globalNotifications.confirmCancel')}
            onClick={() => !sending && setShowConfirm(false)}
          />
          <div
            dir={isRtl ? 'rtl' : 'ltr'}
            className={`relative z-10 w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl ${isRtl ? 'text-right' : 'text-left'}`}
          >
            <h3 className="text-lg font-semibold text-gray-900">{t('globalNotifications.confirmTitle')}</h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">{t('globalNotifications.confirmDescription')}</p>
            <p className="mt-3 text-xs text-gray-500">{t('globalNotifications.confirm')}</p>
            <div className={`mt-6 flex flex-wrap gap-3 ${isRtl ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
              <button
                type="button"
                disabled={sending}
                onClick={() => setShowConfirm(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                {t('globalNotifications.confirmCancel')}
              </button>
              <button
                type="button"
                disabled={sending}
                onClick={() => void runSend()}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-orange px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-600 disabled:opacity-50"
              >
                {sending ? t('globalNotifications.sending') : t('globalNotifications.confirmSend')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
